import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAccounts, createAccount, getTeamUsers } from '../api';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  ShieldAlert,
  X,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { getLevelBadge } from '../components/account/helpers';

const TeamQueue = () => {
  const { user, canCreateAccounts, canReassign } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    level: '',
    stale: '',
    assignedTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showNewAccountModal, setShowNewAccountModal] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);

  const userTeam = user?.team;
  const isManager = user?.role === 'manager';
  const isInboundSales = user?.role === 'inbound_sales';
  const isL2Tech = user?.role === 'l2_tech';
  const isL3Tech = user?.role === 'l3_tech';
  const canViewAllAccounts = isManager || isInboundSales || isL2Tech || isL3Tech;

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (!canViewAllAccounts) {
        params.team = userTeam;
      }
      if (filters.status) params.status = filters.status;
      if (filters.level) params.level = parseInt(filters.level);
      if (filters.stale) params.stale = filters.stale === 'true';
      if (filters.assignedTo) params.assigned_to = filters.assignedTo;
      
      const response = await getAccounts(params);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  }, [canViewAllAccounts, userTeam, filters]);

  const fetchTeamUsers = useCallback(async () => {
    if (!userTeam) return;
    try {
      const response = await getTeamUsers(userTeam);
      setTeamUsers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    }
  }, [userTeam]);

  useEffect(() => {
    fetchAccounts();
    fetchTeamUsers();
  }, [fetchAccounts, fetchTeamUsers]);

  const filteredAccounts = accounts.filter(account => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      account.account_number.toLowerCase().includes(search) ||
      account.account_name.toLowerCase().includes(search) ||
      account.assigned_to_name?.toLowerCase().includes(search)
    );
  });

const getStatusBadge = (status) => {
    const styles = {
      'In Progress': 'bg-primary/10 text-primary border-primary/50',
      'Pending Review': 'bg-purple-500/10 text-purple-400 border-purple-500/50',
      'On Hold': 'bg-zinc-500/10 text-zinc-400 border-zinc-500/50',
      'Blocked': 'bg-destructive/10 text-destructive border-destructive/50',
      'Awaiting Client': 'bg-warning/10 text-warning border-warning/50',
      'Pending L3 Verification': 'bg-orange-500/10 text-orange-400 border-orange-500/50',
      'Completed': 'bg-success/10 text-success border-success/50',
    };
    return styles[status] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/50';
  };

  const getStaleIndicator = (account) => {
    if (!account.stale_flag) return null;
    const colors = {
      1: 'text-l1-stale bg-l1-stale/10',
      2: 'text-l2-stale bg-l2-stale/10',
      3: 'text-l3-stale bg-l3-stale/10',
    };
    return colors[account.account_level];
  };

  const clearFilters = () => {
    setFilters({ status: '', level: '', stale: '', assignedTo: '' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary">
            {canViewAllAccounts ? 'All Accounts' : `${userTeam} Queue`}
          </h1>
          <p className="text-text-secondary text-sm mt-1">
            {filteredAccounts.length} accounts
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAccounts}
            className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-highlight transition-all"
            data-testid="refresh-btn"
          >
            <RefreshCw size={20} />
          </button>
          {canCreateAccounts && (
            <button
              onClick={() => setShowNewAccountModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-all btn-glow"
              data-testid="new-account-btn"
            >
              <Plus size={20} />
              New Account
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={18} />
          <input
            type="text"
            placeholder="Search by account number, name, or assignee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-md text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            data-testid="search-input"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-md border transition-all ${
            showFilters || Object.values(filters).some(v => v)
              ? 'bg-primary/10 text-primary border-primary/50'
              : 'bg-surface text-text-secondary border-border hover:text-text-primary hover:bg-surface-highlight'
          }`}
          data-testid="filter-toggle"
        >
          <Filter size={18} />
          Filters
          {Object.values(filters).some(v => v) && (
            <span className="px-1.5 py-0.5 text-xs bg-primary text-white rounded-full">
              {Object.values(filters).filter(v => v).length}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="p-4 bg-surface border border-border rounded-lg space-y-4" data-testid="filter-panel">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-primary">Filters</span>
            <button
              onClick={clearFilters}
              className="text-xs text-text-secondary hover:text-primary transition-colors"
              data-testid="clear-filters"
            >
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 bg-background border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              <option value="In Progress">In Progress</option>
              <option value="Pending Review">Pending Review</option>
              <option value="On Hold">On Hold</option>
              <option value="Blocked">Blocked</option>
              <option value="Awaiting Client">Awaiting Client</option>
              <option value="Pending L3 Verification">Pending L3</option>
              <option value="Completed">Completed</option>
            </select>
            <select
              value={filters.level}
              onChange={(e) => setFilters({ ...filters, level: e.target.value })}
              className="px-3 py-2 bg-background border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="filter-level"
            >
              <option value="">All Levels</option>
              <option value="1">Level 1</option>
              <option value="2">Level 2</option>
              <option value="3">Level 3</option>
            </select>
            <select
              value={filters.stale}
              onChange={(e) => setFilters({ ...filters, stale: e.target.value })}
              className="px-3 py-2 bg-background border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="filter-stale"
            >
              <option value="">All</option>
              <option value="true">Stale Only</option>
              <option value="false">Not Stale</option>
            </select>
            {canReassign && teamUsers.length > 0 && (
              <select
                value={filters.assignedTo}
                onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                className="px-3 py-2 bg-background border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="filter-assigned"
              >
                <option value="">All Assignees</option>
                {teamUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Accounts Table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="accounts-table">
            <thead>
              <tr className="border-b border-border bg-surface-highlight">
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Account</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Level</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                {canViewAllAccounts && <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Team</th>}
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Assigned To</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Last Activity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Blockers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr>
                  <td colSpan={canViewAllAccounts ? 7 : 6} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center gap-2 text-text-secondary">
                      <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full spinner" />
                      Loading...
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filteredAccounts.length === 0 && (
                <tr>
                  <td colSpan={canViewAllAccounts ? 7 : 6} className="px-4 py-8 text-center text-text-secondary">
                    No accounts found
                  </td>
                </tr>
              )}
              {!loading && filteredAccounts.length > 0 && (
                filteredAccounts.map((account) => {
                  const staleIndicator = getStaleIndicator(account);
                  return (
                    <tr
                      key={account.id}
                      onClick={() => navigate(`/accounts/${account.id}`)}
                      className="cursor-pointer table-row-hover transition-colors"
                      data-testid={`account-row-${account.account_number}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {staleIndicator && (
                            <div className={`p-1 rounded ${staleIndicator}`} title="Stale account">
                              <Clock size={14} />
                            </div>
                          )}
                          <div>
                            <p className="font-mono text-sm font-medium text-text-primary">{account.account_number}</p>
                            <p className="text-sm text-text-secondary truncate max-w-[200px]">{account.account_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getLevelBadge(account.account_level)}`}>
                          L{account.account_level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadge(account.status)}`}>
                          {account.status}
                        </span>
                      </td>
                      {canViewAllAccounts && (
                        <td className="px-4 py-3">
                          <span className="text-sm text-text-secondary">{account.current_team}</span>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-secondary">{account.assigned_to_name || 'Unassigned'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-secondary">
                          {format(new Date(account.last_activity_date), 'MMM d, h:mm a')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {account.blocker_count > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/50">
                            <ShieldAlert size={12} />
                            {account.blocker_count}
                          </span>
                        ) : (
                          <span className="text-sm text-text-secondary">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Account Modal */}
      {showNewAccountModal && (
        <NewAccountModal
          onClose={() => setShowNewAccountModal(false)}
          onSuccess={() => {
            setShowNewAccountModal(false);
            fetchAccounts();
          }}
        />
      )}
    </div>
  );
};

const NewAccountModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    account_number: '',
    account_name: '',
    account_level: 1,
    account_received_date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createAccount(formData);
      toast.success('Account created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="new-account-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">New Account</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-surface-highlight text-text-secondary hover:text-text-primary transition-colors"
            data-testid="close-modal"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Account Number</label>
            <input
              type="text"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., ACC-001"
              data-testid="account-number-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Account Name</label>
            <input
              type="text"
              value={formData.account_name}
              onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Customer name"
              data-testid="account-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Date Received</label>
            <input
              type="date"
              value={formData.account_received_date}
              onChange={(e) => setFormData({ ...formData, account_received_date: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="account-received-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Account Level</label>
            <select
              value={formData.account_level}
              onChange={(e) => setFormData({ ...formData, account_level: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="account-level-select"
            >
              <option value={1}>Level 1</option>
              <option value={2}>Level 2</option>
              <option value={3}>Level 3</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md font-medium hover:bg-border transition-colors"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="create-account-btn"
            >
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamQueue;
